<template>
  <b-container
    class="m-0 p-0"
    fluid>
    <paywall-preview
      :placeholder="placeholder"
      :device="device"
      :offer-group-index.sync="offerGroupIndex"
      :config="selectedConfig" />
    <hr class="m-3 mt-5">
    <b-form
      ref="form"
      class="p-3"
      novalidate
      :validated="validated">
      <paywall-form-input
        size="lg"
        :config="selectedConfig"
        :device="device"
        :placeholder="selectedConfig.header"
        :char-max="selectedConfig.rules.header.charMax"
        label="Header"
        config-key="header"
        required />
      <paywall-form-input
        :config="selectedConfig"
        :device="device"
        :placeholder="selectedConfig.subheader"
        :char-max="selectedConfig.rules.subheader.charMax"
        label="Subheader"
        config-key="subheader" />
      <paywall-form-input
        size="lg"
        :config="selectedConfig"
        :device="device"
        :placeholder="selectedConfig.footer"
        :char-max="selectedConfig.rules.footer.charMax"
        label="Footer"
        config-key="footer"
        required />
      <paywall-form-input
        size="lg"
        :config="selectedConfig"
        :device="device"
        :placeholder="selectedConfig.platformUrl"
        :char-max="selectedConfig.rules.platformUrl.charMax"
        label="Platform URL"
        config-key="platformUrl"
        required />
      <b-tabs
        v-model="offerGroupIndex"
        @input="onOfferGroupTabSwitch">
        <b-tab
          v-for="(offerGroup, index) in selectedConfig.offerGroups"
          :key="index">
          <template #title>
            <paywall-form-input-editable
              v-model="offerGroup.name"
              :in-edit-mode="offerGroupEditMode[index]"
              tag="span"
              @set-edit-mode="$set(offerGroupEditMode, index, $event)" />
            <b-dropdown
              v-if="index === offerGroupIndex && !offerGroupEditMode[index]"
              dropright
              no-caret
              size="sm"
              variant="link"
              @shown="offerGroupDropDownIcon = 'x'"
              @hidden="offerGroupDropDownIcon = 'three-dots-vertical'">
              <template #button-content>
                <b-icon :icon="offerGroupDropDownIcon" />
              </template>
              <b-dropdown-item @click.prevent="onRenameOfferGroup(index)">
                <b-icon icon="pencil-square" /> Rename
              </b-dropdown-item>
              <b-dropdown-item
                variant="danger"
                :disabled="selectedConfig.offerGroups.length <= minOfferGroups"
                @click.prevent="onDeleteOfferGroup(index)">
                <b-icon icon="trash" /> Delete
              </b-dropdown-item>
            </b-dropdown>
          </template>

          <paywall-form-packages
            :placeholder="placeholder"
            :disabled="!canPaywallEdit"
            :config="selectedConfig"
            :device="device"
            :offer-group-index="index"
            config-key="packages" />
        </b-tab>

        <template #tabs-end>
          <b-nav-item
            role="presentation"
            href="#"
            :disabled="selectedConfig.offerGroups.length >= maxOfferGroups"
            @click.prevent="onAddOfferGroup">
            <b-icon icon="plus-circle-fill" />
          </b-nav-item>
        </template>
      </b-tabs>
    </b-form>
  </b-container>
</template>

<script>
import {mapGetters} from "vuex"
import PaywallFormPackages from "@/components/PaywallV1/PaywallFormPackages"
import PaywallFormInput from "@/components/PaywallV1/PaywallFormInput"
import PaywallFormInputEditable from "@/components/PaywallV1/PaywallFormInputEditable"
import PaywallPreview from "@/components/PaywallV1/PaywallPreview"

export default {
  name: "PaywallForm",
  components: {
    PaywallPreview,
    PaywallFormPackages,
    PaywallFormInput,
    PaywallFormInputEditable,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    device: {
      type: String,
      required: true,
    },
    placeholder: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    validated: false,
    offerGroupIndex: 0,
    // Currently the clients only support a maximum of two offer group tabs, but this could change later on.
    maxOfferGroups: 2,
    minOfferGroups: 1,
    offerGroupDropDownIcon: "three-dots-vertical",
    offerGroupEditMode: {},
  }),
  computed: {
    ...mapGetters(['canPaywallEdit']),
    selectedConfig() {
      return this.config[this.device]
    },
  },
  methods: {
    isValid() {
      this.validated = true
      return this.$refs.form.checkValidity()
    },
    onOfferGroupTabSwitch(index) {
      this.offerGroupIndex = index
    },
    onAddOfferGroup() {
      this.selectedConfig.offerGroups.push(
          {
            name: "New Offer Group",
            packages: [
              {
                enabled: true,
                title: "MLB Account",
                subtitle: "Free for 7 days",
                text: "Watch the MLB.TV Free MLB Game of the Day.",
                type: "free",
                price: "Free",
                priceUnit: "/month",
                requiresTeamSelection: false,
                purchaseLink: `mlb.com/${this.device}`,
                buttons: [
                  {
                    text: "button one",
                    enabled: true,
                  },
                  {
                    text: "button two",
                    enabled: false,
                  },
                ],
                tags: [
                  {
                    name: "Best Value",
                    bgColor: "#006EF5",
                    fgColor: "#FFFFFF",
                  },
                ],
                size: "medium",
              },
            ],
          },
       )
    },
    async onDeleteOfferGroup(index) {
      const modalConfirmed = await this.$confirm.open(`Are you sure you want to delete the offer group "${this.selectedConfig.offerGroups[index].name}"?`)
      if (!modalConfirmed) {
        return
      }
      this.selectedConfig.offerGroups.splice(index, 1)
    },
    onRenameOfferGroup(index) {
      // The hidden event does not fire when entering edit mode, so the icon is manually reset.
      this.offerGroupDropDownIcon = "three-dots-vertical"
      this.$set(this.offerGroupEditMode, index, true)
    },
  },
}
</script>

<style scoped>
/deep/ .nav {
  width: unset !important;
}
</style>
